import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import stylesModule from './Filters.module.less';
import { Button, Checkbox, Divider, Input, Space, Tree } from 'antd';
import { useCachedData } from '../../../../connection/CacheContext';
import { SearchQuery, useSearchContext } from '../../SearchContext';
import { FilterTitle } from './FilterTitle';

const styles: any = stylesModule;

export type LocationsValue = {
  country?: string[];
  location?: string[];
};

type Props = {
  value?: LocationsValue;
  title: string;
  onChange?: (value?: LocationsValue) => void;
  getQuery?: (value?: LocationsValue) => SearchQuery;
  onCancel?: () => void;
};

export const LocationFilter: FC<Props> = (props) => {
  const { onChange } = props;
  const [searchText, setSearchText] = useState<string>();
  const { masterdata, masterdataById, activeEnumValue, mainType } = useCachedData();
  const [checked, setChecked] = useState<string[]>();
  const [checked2, setChecked2] = useState<string[]>();
  const [allChecked, setAllChecked] = useState<boolean>(true);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const ctx = useSearchContext();
  const [sriLanka, setSriLanka] = useState<string>('');
  const [treeData, listData] = useMemo(() => {
    const lanka = masterdata[mainType['Country']].find((c) => c.code === 'LK')!;
    setSriLanka(lanka.id);
    const countryCountText = (id: string) => {
      const c = (ctx.results?.aggs as any)?.['country']?.[id];
      return c === undefined || c === 0 ? '' : ` (${c})`;
    };
    const countries = [
      {
        _s: '000000',
        _c: countryCountText(lanka.id) === '' ? 1 : 0,
        title: `${lanka.value}${countryCountText(lanka.id)}`,
        key: `c-${lanka.id}`,
        children: [] as any[],
      },
      ...masterdata[mainType['Country']]
        .filter((c) => c.id !== lanka.id)
        .sort((a, b) => a.value.localeCompare(b.value))
        .map((c) => [c, (ctx.results?.aggs as any)?.['country']?.[c.id] ?? 0])
        .filter((c) => c[1] > 0)
        .map((c) => ({
          _s: c[0].value,
          _c: c[1] === 0 ? 1 : 0,
          title: `${c[0].value}${(c[1] ?? 0) === 0 ? '' : ` (${c[1]})`}`,
          key: `c-${c[0].id}`,
          children: [] as any[],
        })),
    ];

    const countText = (id: string) => {
      const c = (ctx.results?.aggs as any)?.['location']?.[id];
      return c === undefined || c === 0 ? '' : `${c}`;
    };

    masterdata[mainType['Location']]
      .filter((v) => v.status === 'Active')
      .forEach((v) => {
        const country = countries.find((c) => c.key === `c-${v?.country ?? lanka.id}`);
        if (country) {
          const ctext = countText(v.id);
          if (parseInt(ctext) > 0) {
            country?.children.push({
              _c: ctext === '' ? 1 : 0,
              title: `${v.value} (${ctext})`,
              key: `${country.key.split('-')[1]}-${v.id}`,
            });
          }
        }
      });
    return [
      countries,
      [
        ...countries.map((c) => ({
          title: c.title as string,
          key: c.key as string,
          tl: c.title.toLowerCase(),
        })),
        ...countries.flatMap((c) =>
          (c.children ?? []).map((t) => ({
            title: t.title as string,
            key: t.key as string,
            tl: t.title.toLowerCase(),
          })),
        ),
      ].sort((a, b) => a.title.localeCompare(b.title)),
    ];
  }, [ctx.results?.aggs, mainType, masterdata]);

  const sortedTreeData = useMemo(() => {
    const tds = treeData.map((td) => {
      const children = (td.children ?? []).map((tc) => ({
        ...tc,
        _c: (checked2 ?? []).indexOf(tc.key) === -1 ? 1 : 0,
      }));
      return {
        ...td,
        _c:
          (checked2 ?? []).indexOf(td.key) === -1 && children.findIndex((c) => c._c === 0) === -1
            ? 1
            : 0,
        children,
      };
    });
    const countries = tds.sort((a, b) => {
      if (a._c === b._c) {
        return a._s.localeCompare(b._s);
      }
      return a._c - b._c;
    });
    countries.forEach((c) => {
      c.children = c.children.sort((a, b) => {
        if (a._c === b._c) {
          return a.title.localeCompare(b.title);
        }
        return a._c - b._c;
      });
    });
    console.log('Countries', countries);
    return countries;
  }, [checked2, treeData]);

  const handleChecked = useCallback(
    (v: string[]) => {
      if (v.length === 0) {
        onChange?.(undefined);
        return;
      }
      const selectedCountries = v
        .map((x) => x.split('-'))
        .filter((x) => x[0] === 'c')
        .map((x) => x[1]);
      if (selectedCountries.length === treeData.length) {
        onChange?.(undefined);
        return;
      }
      const newVal = {
        location: v
          .map((x) => x.split('-'))
          .filter((x) => x[0] !== 'c')
          .filter((x) => selectedCountries.indexOf(x[0]) === -1)
          .map((x) => x[1]),
        country: selectedCountries,
      };
      onChange?.(newVal);
    },
    [onChange, treeData.length],
  );

  useEffect(() => {
    if (!props.value) {
      setAllChecked(true);
      setChecked([]);
      setChecked2([]);
      return;
    }
    const getCountryId = (locationId: string) =>
      masterdataById[mainType['Location']][locationId].country ?? sriLanka;
    const chkd = [
      ...(props.value?.country?.map((v) => `c-${v}`) ?? []),
      ...(props.value?.location?.map((v) => `${getCountryId(v)}-${v}`) ?? []),
    ];
    setAllChecked(chkd.length === 0 || props.value?.country?.length === treeData.length);
    setChecked(chkd);
    setChecked2(chkd);
  }, [mainType, masterdataById, props.value, sriLanka, treeData]);

  const handleCheckChange = useCallback(
    (val: string[]) => {
      setChecked(val);
      setAllChecked(
        val.length === 0 || val?.filter((s) => s.split('-')[0] === 'c').length === treeData.length,
      );
    },
    [treeData.length],
  );

  const filteredData = useMemo(() => {
    if (!searchText || searchText.trim() === '') {
      return listData;
    }
    const txt = searchText.toLowerCase().trim();
    return listData.filter((l) => l.tl.indexOf(txt) !== -1);
  }, [listData, searchText]);

  return (
    <Space size={16} direction="vertical" className={styles.filterArea}>
      {/*<FilterTitle*/}
      {/*  title={props.title}*/}
      {/*  onApply={() => onChange?.(value && value.length > 0 ? value : undefined)}*/}
      {/*  onCancel={props.onCancel}*/}
      {/*/>*/}
      <FilterTitle
        id={'location-filter'}
        title={'Location'}
        onApply={() => handleChecked?.(checked ?? [])}
        onCancel={props.onCancel}
      />
      <Input
        placeholder={'Search'}
        allowClear
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
      />
      <div className={styles.filterAreaSearch}>
        <Space direction="vertical">
          {(searchText ?? '') === '' ? (
            <>
              <Checkbox
                key={'all'}
                value={'all'}
                checked={allChecked}
                onChange={(e) => {
                  handleCheckChange([]);
                }}>
                All Locations
              </Checkbox>
              <Divider style={{ margin: 0 }} />
              <Tree
                checkable
                selectable={false}
                onCheck={(v) => handleCheckChange(v as any)}
                checkedKeys={checked}
                treeData={sortedTreeData}
                autoExpandParent={true}
                expandedKeys={expandedKeys}
                onExpand={(exK: any) => setExpandedKeys(exK)}
              />
            </>
          ) : (
            <>
              {filteredData.map((val) => (
                <Checkbox
                  onChange={(e) =>
                    setChecked(
                      e.target.checked
                        ? [...(checked ?? []), val.key]
                        : (checked ?? []).filter((v) => v !== val.key),
                    )
                  }
                  checked={(checked ?? []).indexOf(val.key) !== -1}
                  key={val.key}
                  value={val.key}>
                  {val.title}
                </Checkbox>
              ))}
            </>
          )}
        </Space>
      </div>
      <Button
        className={styles.applyButton}
        type={'primary'}
        size={'small'}
        onClick={() => handleChecked?.(checked ?? [])}>
        Apply
      </Button>
    </Space>
  );
};
